import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface PolicyRow {
  readonly policyId: string;
  readonly policyCode: string;
  readonly policyName: string;
  readonly policyCategory: string;
  readonly policyType: string;
  readonly status: string;
  readonly classification: string;
  readonly version: string;
}

const CATEGORIES = ['', 'INFORMATION_SECURITY', 'CYBER_SECURITY', 'DATA_PROTECTION', 'PRIVACY', 'ACCESS_CONTROL', 'NETWORK_SECURITY', 'INCIDENT_RESPONSE', 'BUSINESS_CONTINUITY', 'THIRD_PARTY', 'ACCEPTABLE_USE', 'COMPLIANCE'];
const STATUSES = ['', 'DRAFT', 'UNDER_REVIEW', 'APPROVED', 'PUBLISHED', 'DEPRECATED', 'RETIRED'];
const CREATE_CATEGORIES = CATEGORIES.filter(Boolean);

/**
 * M30 — Policy Repository (§18 / §31). Central catalogue of security policies,
 * standards, procedures and guidelines with their governance status, version
 * and classification. Supports the draft → review → approve → publish workflow
 * and mandatory periodic reviews (BR-07) with user acknowledgements — authoring,
 * publish, review, acknowledge and delete are wired to the guarded
 * /v1/security/policies write API.
 */
export function M30PolicyRepositoryPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<PolicyRow[]>([]);
  const [policyCategory, setPolicyCategory] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  // create form
  const [name, setName] = useState('');
  const [newCategory, setNewCategory] = useState('INFORMATION_SECURITY');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (policyCategory) params.set('policyCategory', policyCategory);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: PolicyRow[] }>(`/v1/security/policies${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, policyCategory, status, reload]);

  const failMessage = (err: unknown, verb: string, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m30.policy.notAuthorised', { verb, perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!name.trim()) { setActionError(t('m30.policy.nameRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: PolicyRow }>(`/v1/security/policies`, {
        method: 'POST',
        body: { policyName: name.trim(), policyCategory: newCategory },
        token,
      });
      setNotice(t('m30.policy.created', { code: r.data.policyCode }));
      setName('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, t('m30.policy.verbCreate'), 'M30_POLICY_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const runRowAction = async (
    fn: () => Promise<unknown>,
    successMsg: string,
    verb: string,
    perm: string,
  ): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(successMsg);
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, verb, perm));
    }
  };

  const publish = (p: PolicyRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/security/policies/${p.policyId}/publish`, { method: 'POST', body: {}, token }),
      t('m30.policy.published', { code: p.policyCode }), t('m30.policy.verbPublish'), 'M30_POLICY_PUBLISH',
    );

  const review = (p: PolicyRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/security/policies/${p.policyId}/review`, { method: 'POST', body: {}, token }),
      t('m30.policy.reviewed', { code: p.policyCode }), t('m30.policy.verbReview'), 'M30_POLICY_MANAGE',
    );

  const acknowledge = (p: PolicyRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/security/policies/${p.policyId}/acknowledge`, { method: 'POST', body: {}, token }),
      t('m30.policy.acknowledged', { code: p.policyCode }), t('m30.policy.verbAcknowledge'), 'M30_POLICY_VIEW',
    );

  const remove = (p: PolicyRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/security/policies/${p.policyId}`, { method: 'DELETE', token }),
      t('m30.policy.deleted', { code: p.policyCode }), t('m30.policy.verbDelete'), 'M30_POLICY_MANAGE',
    );

  return (
    <main aria-label="policy-repository">
      <h1>{t('m30.policy.title')}</h1>

      <section aria-label="create-policy">
        <h2>{t('m30.policy.newHeading')}</h2>
        <form aria-label="policy-form" onSubmit={(e) => void create(e)}>
          <label>{t('m30.policy.fieldName')}<input aria-label="field-policy-name" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label>{t('m30.policy.fieldCategory')}
            <select aria-label="field-policy-category" value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
              {CREATE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-policy" disabled={busy}>{busy ? t('m30.policy.submitCreating') : t('m30.policy.submitCreate')}</button>
        </form>
        {actionError && <p role="alert" aria-label="policy-error">{actionError}</p>}
        {notice && <p aria-label="policy-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m30.policy.filterCategory')}
          <select aria-label="filter-policy-category" value={policyCategory} onChange={(e) => setPolicyCategory(e.target.value)}>
            {CATEGORIES.map((c) => <option key={c || 'all'} value={c}>{c || t('m30.policy.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m30.policy.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m30.policy.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m30.policy.loading')}</p> : (
        <>
          <p aria-label="policy-count">{t('m30.policy.count', { count: rows.length })}</p>
          <table aria-label="policy-table">
            <thead><tr><th>{t('m30.policy.thCode')}</th><th>{t('m30.policy.thName')}</th><th>{t('m30.policy.thCategory')}</th><th>{t('m30.policy.thType')}</th><th>{t('m30.policy.thStatus')}</th><th>{t('m30.policy.thClassification')}</th><th>{t('m30.policy.thVersion')}</th><th>{t('m30.policy.thActions')}</th></tr></thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.policyId}>
                  <td>{p.policyCode}</td><td>{p.policyName}</td><td>{p.policyCategory}</td>
                  <td>{p.policyType}</td><td>{p.status}</td><td>{p.classification}</td><td>{p.version}</td>
                  <td>
                    <button type="button" aria-label={`review-${p.policyCode}`} onClick={() => void review(p)}>{t('m30.policy.btnReview')}</button>
                    <button type="button" aria-label={`publish-${p.policyCode}`} onClick={() => void publish(p)}>{t('m30.policy.btnPublish')}</button>
                    <button type="button" aria-label={`acknowledge-${p.policyCode}`} onClick={() => void acknowledge(p)}>{t('m30.policy.btnAcknowledge')}</button>
                    <button type="button" aria-label={`delete-${p.policyCode}`} onClick={() => void remove(p)}>{t('m30.policy.btnDelete')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
